export type StreamType = "hls" | "ts" | "mp4" | "unknown";

export interface M3UChannel {
  name: string;
  url: string;
  tvgId: string | null;
  tvgName: string | null;
  logo: string | null;
  group: string;
  streamType: StreamType;
}

function detectStreamType(url: string): StreamType {
  const lower = url.toLowerCase().split("?")[0];
  if (lower.endsWith(".m3u8")) return "hls";
  if (lower.endsWith(".ts")) return "ts";
  if (lower.endsWith(".mp4")) return "mp4";
  return "unknown";
}

export function parseM3U(text: string): M3UChannel[] {
  const lines = text.split(/\r?\n/);
  const channels: M3UChannel[] = [];
  let pending: Partial<M3UChannel> | null = null;

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    if (line.startsWith("#EXTM3U")) continue;
    if (line.startsWith("#EXTINF:")) {
      // Format: #EXTINF:-1 attr1="v1" attr2="v2",Name
      const commaIdx = line.indexOf(",");
      const attrPart = commaIdx > 0 ? line.substring(8, commaIdx) : line.substring(8);
      const name = commaIdx > 0 ? line.substring(commaIdx + 1).trim() : "Unknown";

      const tvgId = matchAttr(attrPart, "tvg-id");
      const tvgName = matchAttr(attrPart, "tvg-name");
      const logo = matchAttr(attrPart, "tvg-logo");
      const group = matchAttr(attrPart, "group-title") ?? "Default";

      pending = { name, tvgId, tvgName, logo, group };
    } else if (!line.startsWith("#")) {
      if (pending) {
        channels.push({
          name: pending.name ?? "Unknown",
          url: line,
          tvgId: pending.tvgId ?? null,
          tvgName: pending.tvgName ?? null,
          logo: pending.logo ?? null,
          group: pending.group ?? "Default",
          streamType: detectStreamType(line),
        });
        pending = null;
      }
    }
  }
  return channels;
}

function matchAttr(s: string, name: string): string | null {
  const re = new RegExp(`${name}="([^"]*)"`, "i");
  const m = s.match(re);
  return m ? m[1] : null;
}

export async function fetchAndParseM3U(url: string): Promise<M3UChannel[]> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`M3U fetch failed: HTTP ${res.status}`);
  const text = await res.text();
  return parseM3U(text);
}
